import type { ShelemMove, ShelemView } from "@bing/engine-shelem";
import { useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Avatar } from "../components/Avatar";
import { atY, SEAT_AVATAR } from "../game/felt-geometry";
import type { SeatAvatar } from "../game/seat-identity";
import { TABLE_BTN } from "../game/table-parts";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import type { SfxName } from "../sound/sfx";
import { FELT_INSET, PARTNER_TOP_IN_FELT, SIDE_NAME_MAX } from "./shelem-felt";

/* ------------------------------------------------------------------ *
 * شلم's AUCTION PARTS (P1 Task 4) — the seat's bid / pass chip, the felt's one-line statement and
 * the bidding row — drawn by `ShelemTable` (moved here unchanged, Task 5: the table had grown to
 * ~1500 lines). Every control is still derived from the server's `legal` list by the table, which
 * hands the row what it draws and the two callbacks it calls.
 * ------------------------------------------------------------------ */

/** A ladder selection: a numeric bid, or the شلم call. */
export type BidChoice = number | "shelem";

/** A seat's auction badge: its gap over the avatar, and the air it keeps inside the felt's gold ring
 *  (`AuctionBadge`). */
export const BADGE_GAP = 3;
const BADGE_RING_AIR = 2;
/** …which tightens to the probe's own 1 px (`*-felt-edge`) only to keep the dealer's marker in sight
 *  (card-67 D, below). */
const BADGE_RING_AIR_MIN = 1;
/** The badge's height at a text zoom: its 11 px `leading-none` line and `py-px` (the کف's corner keeps
 *  clear of the right seat's, card-67 C4). */
export const badgeHeight = (zoom: number): number => 11 * zoom + 2;

/** P0-3: every number on the ladder reads as its number — ۱۶۵ is a bid, never «شلم»; only the call
 *  itself reads «شلم». */
function bidLabel(choice: BidChoice): string {
  return choice === "shelem" ? fa.shelem.title : faDigits(choice);
}

/**
 * The least top a badge may have inside the felt, its air kept: under the felt's straight top, or
 * where the badge reaches into a ROUNDED top corner, under that corner's arc (iteration 4: at 320×568
 * ×1.5 a side seat's badge, x 258.7–297.3, sat 2 px under the straight top yet 3.9 px ON the ring of
 * the top-right 40 px corner). The badge is a pill (`rounded-full`) spanning `left`…`right`: its ends
 * are circles of radius `r`, so the test is the probe's — its core's corner within `R − r − air` of the
 * corner's centre (at the corner's start that is the straight top's `top + air`). A corner the core
 * reaches past entirely: the arc's foot.
 */
function badgeTopMin(
  left: number,
  right: number,
  r: number,
  felt: HTMLElement,
  f: DOMRect,
  air: number,
): number {
  const cs = getComputedStyle(felt);
  const arc = (R: number, dx: number): number => {
    const room = R - r - air;
    if (!(R > 0) || dx <= 0) return f.top + air;
    return dx >= room ? f.top + R - r : f.top + R - r - Math.sqrt(room * room - dx * dx);
  };
  const rl = parseFloat(cs.borderTopLeftRadius) || 0;
  const rr = parseFloat(cs.borderTopRightRadius) || 0;
  return Math.max(arc(rl, f.left + rl - (left + r)), arc(rr, right - r - (f.right - rr)));
}

/** Where a badge sits: `drop` px down from its 3 px over its avatar, `slide` px sideways off its
 *  avatar's centre line (+ to the physical right). */
interface BadgePlace {
  readonly drop: number;
  readonly slide: number;
}
const BADGE_UNMOVED: BadgePlace = { drop: 0, slide: 0 };
/** How far a badge may slide off its avatar to clear the dealer's marker (16 px on the narrowest named
 *  felt, 300×667 under a 150 % zoom), and no further. */
const BADGE_SLIDE_MAX = 64;

/**
 * A seat's bid («۱۳۵») or «پاس» while the auction runs — a chip ABOVE its avatar, out of flow, a
 * child of the seat's root (the probe reads a seat's badges off its root; the «حاکم» chip's slot is
 * inside the ring). Under the name it hung 34 px below the seat: on a short felt (320×568) the side
 * seats' badges reached the bidding row and the partner's the centre (Task 3: `bid-seat` 236). Above
 * the avatar every seat has room — the partner's 38 px top keeps it 3 px off the felt's gold ring up
 * to a 150 % text zoom (11 px, `leading-none`, 1 px of padding: 18.5 px tall at 1.5). A pass is
 * greyed; a bid wears the team's primary container.
 */
export function AuctionBadge({ view, seat }: { view: ShelemView; seat: number }): ReactNode {
  const passed = view.passed[seat] === true;
  const bid = view.bids[seat] ?? null;
  // …and never ON the gold ring (Task 9b: a side seat's two-line notice lifts its avatar 12 px on a short
  // felt — at 320×568 ×1.5 the badge sat 4.7 px into the ring): measured against the felt
  // (`[data-felt]`), the badge drops just inside it — over its avatar's top edge, its own seat's part
  // …AND NEVER OVER THE DEALER'S MARKER (card-67 D, the B+C review: the marker sits on the ring's
  // upper-right since B4, and on a short felt — 320×568 / 300×667 under a 130–150 % zoom, your
  // bidding row up — a side seat's dropped «پاس», wider than the ring, covered up to 80 % of it):
  // where the drop would bring the badge down onto the marker, the badge SLIDES toward the felt's
  // middle by the least that clears it — away from the rounded corner, so it rises above the
  // marker's top (its air over the felt's edge tightening from 2 px to 1 where that is what it
  // lacks), or past the marker's side. A seat without the marker never slides.
  const ref = useRef<HTMLSpanElement | null>(null);
  const [place, setPlace] = useState<BadgePlace>(BADGE_UNMOVED);
  useLayoutEffect(() => {
    const el = ref.current;
    const anchor = (el?.offsetParent as HTMLElement | null) ?? el?.parentElement ?? null;
    const felt = el?.closest("main")?.querySelector<HTMLElement>("[data-felt]");
    if (!el || !anchor || !felt) return;
    const f = felt.getBoundingClientRect();
    if (!(f.height > 0)) return; // unlaid-out (happy-dom, a hidden page): where it is
    // where `bottom: calc(100% + 3px)` and `left-1/2 -translate-x-1/2` put it — off its anchor, so
    // neither a drop nor a slide ever feeds the next
    const a = anchor.getBoundingClientRect();
    const h = el.offsetHeight;
    const w = el.getBoundingClientRect().width;
    const r = Math.min(h, w) / 2;
    const top = a.top - BADGE_GAP - h;
    const centre = a.left + a.width / 2;
    const at = (slide: number, air = BADGE_RING_AIR): BadgePlace => {
      const left = centre - w / 2 + slide;
      const drop = Math.max(0, Math.ceil(badgeTopMin(left, left + w, r, felt, f, air) - top));
      return { drop, slide };
    };
    const m =
      anchor.querySelector<HTMLElement>('[data-testid="dealer-marker"]')?.getBoundingClientRect() ??
      null;
    const covers = (p: BadgePlace): boolean => {
      if (m === null || !(m.width > 0 && m.height > 0)) return false;
      const left = centre - w / 2 + p.slide;
      return top + h + p.drop > m.top + 0.5 && left + w > m.left + 0.5 && left < m.right - 0.5;
    };
    const clear = (): BadgePlace => {
      const toward = (f.left + f.right) / 2 >= centre ? 1 : -1;
      for (let s = 0; s <= BADGE_SLIDE_MAX; s += 1)
        for (const air of [BADGE_RING_AIR, BADGE_RING_AIR_MIN]) {
          const p = at(toward * s, air);
          if (!covers(p)) return p;
        }
      return at(0); // nothing clears it within reach: where it always was
    };
    const unmoved = at(0);
    const next = covers(unmoved) ? clear() : unmoved;
    if (next.drop !== place.drop || next.slide !== place.slide) setPlace(next);
  });
  if (!passed && bid === null) return null;
  const tone = passed
    ? "bg-surface-container-high text-on-surface-variant"
    : "bg-primary-container text-on-primary-container";
  return (
    <span
      className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-1.5 py-px text-[11px] font-extrabold leading-none shadow-md ${tone}`}
      ref={ref}
      style={{
        bottom: place.drop > 0 ? `calc(100% + 3px - ${place.drop}px)` : "calc(100% + 3px)",
        left: place.slide !== 0 ? `calc(50% + ${place.slide}px)` : undefined,
      }}
    >
      {passed ? fa.shelem.pass : faDigits(bid ?? 0)}
    </span>
  );
}

/*
 * THE AUCTION'S CENTRE — one line, «بالاترین: ۱۳۵ —» and the high bidder's name (its own
 * `dir="auto"` span, cut when short of room), in a pill; «هنوز پیشنهادی نیست» before anyone has bid.
 * WHERE it sits is MEASURED (`auctionCentrePlacement`, off `main`'s laid-out height): the felt's
 * middle is the seats' — the partner's column reaches 91–104 px into the felt (the avatar, its ring
 * and the name box, by zoom and screen) and the side seats' columns, centred on the felt's middle,
 * leave a gap of only ~180 px between them at every phone width. So:
 *   wide   where the band BELOW the side seats' name boxes holds the pill (every phone ≥ ~600 px
 *          tall: 384×823 has 120 px of it under the bidding row), the pill sits centred in that band
 *          with the felt's width — a long name reads whole;
 *   gap    else (320×568 under the row) it sits in the middle gap, just under the partner's column,
 *          no wider than the gap (`CENTRE_GAP_MAX`: the felt less the side columns and 4 px of air
 *          each side) — and the statement drops the word «بالاترین:» (`fa.shelem.highestShort`:
 *          «۱۳۵ —», then the name), so the name keeps the room the word took: with the full label
 *          a 176 px pill at 130 % left it 38.6 px — «Ba…» (the Task 4 review's ruling, fix 1).
 *          The name is still cut to what is left, at its end (`dir="auto"` + `truncate`).
 * Unmeasured (happy-dom, a hidden page): the gap form at `max(floor, 50% − half the pill)`. The
 * numbers: the seat root's height = the ring (avatar + 2 × 2 px) + the column's gap + the name box
 * (the caption's 16 px line × zoom + 4 px of padding); the partner's root top is 38 px into `main`
 * (26 into the felt); the side roots are centred on `main`'s middle. The pill is 38 px tall (the
 * widow's old 30 px line + 8 px of padding) up to a 140 % zoom, the text line + 10 past it.
 */
const PILL_PAD_PX = 10;
const PILL_LINE_PX = 20;
/** A seat's name box line: the caption's 16 px × zoom (a wrapped notice adds one). */
export const NAME_LINE_PX = 16;
const PILL_MIN_H = 38;
/** The air between the felt's statement and the seats' columns. */
export const CENTRE_AIR_PX = 4;
/** The gap between the side columns: the felt less 2 × (the column's reach past the felt's edge —
 *  16 px from the table's edge, so 4 into the felt, plus the root's width: at least `TableSeat`'s
 *  52 px, else its name box's cap — plus 4 px of air). Inline: a `clamp()` inside a class dies. */
export const CENTRE_GAP_MAX = `calc(100% - 16px - 2 * max(52px, ${SIDE_NAME_MAX}))`;
/** The WIDE form's cap: the felt less 24 px a side, clear of its rounded corners — the felt's
 *  statement in the band below the side seats. */
export const CENTRE_WIDE_MAX = "calc(100% - 48px)";

/** A seat root's height on this screen at this zoom (see above). */
export function seatRootHeight(compact: boolean, zoom: number): number {
  const ring = (compact ? SEAT_AVATAR.compact : SEAT_AVATAR.regular) + 4;
  const gap = compact ? 4 : 6;
  return ring + gap + NAME_LINE_PX * zoom + 4;
}

/** Where the side seats' columns end, felt-relative: their roots are centred on the felt's middle. */
export function sideColumnsBottom(feltH: number, compact: boolean, zoom: number): number {
  return feltH / 2 + seatRootHeight(compact, zoom) / 2;
}

/** The pill's height at a text zoom. */
function pillHeight(zoom: number): number {
  return Math.max(PILL_MIN_H, PILL_LINE_PX * zoom + PILL_PAD_PX);
}

/** The pill as it LAYS OUT — its line and `py-1`'s 8 px, at least its 38 px floor (`pillHeight`
 *  keeps 2 px more for the band): 38 at a 150 % zoom, as the probe recorded it. */
const laidOutPill = (zoom: number): number => Math.max(PILL_MIN_H, PILL_LINE_PX * zoom + 8);

/**
 * Where the auction's centre sits (felt coordinates) and how wide it may be — `wide` under the side
 * seats when the band below them holds the pill, else `gap` under the partner's column. (P1 Task 6's
 * `reserveBottom` — the hand's banner at the felt's foot, up into the next auction — went with that
 * banner in Task 7: the hand's end is the hand-end card's window, and the auction waits for it.)
 */
export function auctionCentrePlacement(
  mainH: number | null,
  compact: boolean,
  zoom: number,
  /** A side seat carries a notice — it wraps onto a SECOND line (`wrapNotice`; Task 9b, iteration 1:
   *  the right seat's two-line «از میز رفت» reached 4 px into a wide pill placed off a one-line
   *  column at 320×568 ×1, and so at 384×590 / 384×590@1.25 / 384×569@1.5 ×1.5). */
  sideNotice = false,
): { form: "wide" | "gap" | "tight"; top: string; maxWidth: string } {
  const pill = pillHeight(zoom);
  const root = seatRootHeight(compact, zoom);
  const floor = PARTNER_TOP_IN_FELT + root + CENTRE_AIR_PX;
  if (mainH === null)
    return {
      form: "gap",
      top: `max(${floor.toFixed(1)}px, calc(50% - ${pill / 2}px))`,
      maxWidth: CENTRE_GAP_MAX,
    };
  const feltH = mainH - 2 * FELT_INSET;
  // the root is centred on the felt's middle: a second line moves its bottom half a line down
  const sideBottom =
    sideColumnsBottom(feltH, compact, zoom) + (sideNotice ? (NAME_LINE_PX * zoom) / 2 : 0);
  const bandTop = sideBottom + CENTRE_AIR_PX;
  const bandBottom = feltH - 1;
  if (bandBottom - bandTop >= pill)
    return {
      form: "wide",
      top: `${(bandTop + (bandBottom - bandTop - pill) / 2).toFixed(1)}px`,
      maxWidth: CENTRE_WIDE_MAX,
    };
  const top = Math.max(floor, feltH / 2 - pill / 2);
  // …and where even that pill would run past the felt's foot, the TIGHT one at the partner's floor —
  // the line alone (no vertical padding, no 38 px floor), 8 px a side as the snug pill's (Task 9b,
  // iteration 4: 300×667 under a 150 % zoom with your bidding row up, a 133 px felt — the pill at the
  // floor, 98, ended 3 px past it, clipped). Only there among the named sizes: 320×568's 38 px pill
  // ends at 136 of its 138. (The felt's middle never wins here: a felt that short has it above the
  // floor.)
  if (top + laidOutPill(zoom) > bandBottom)
    return { form: "tight", top: `${floor.toFixed(1)}px`, maxWidth: CENTRE_GAP_MAX };
  return { form: "gap", top: `${top.toFixed(1)}px`, maxWidth: CENTRE_GAP_MAX };
}

/**
 * DOES THE FELT HOLD THE AUCTION'S STATEMENT AT ALL (B3 item 7, operator 2026-10-20: «I play on
 * Telegram Desktop zoomed in»)? The tight form's one line under the partner's column, inside the
 * felt — where it does not (Telegram Desktop 320×492@1.5, 307×472@1.25, 256×393@1.5 with your
 * bidding row up: the felt shorter than the partner's column and a line), the pill was laid past the
 * felt's foot and its `overflow-hidden` cut it away, so the high bid was nowhere to read on the felt.
 * Your bidding row then carries the statement instead (`BidRow`'s `statement`). Unmeasured: it fits.
 */
export function auctionCentreFits(
  mainH: number | null,
  compact: boolean,
  zoom: number,
  sideNotice = false,
): boolean {
  if (mainH === null) return true;
  if (auctionCentrePlacement(mainH, compact, zoom, sideNotice).form !== "tight") return true;
  const floor = PARTNER_TOP_IN_FELT + seatRootHeight(compact, zoom) + CENTRE_AIR_PX;
  return floor + PILL_LINE_PX * zoom <= mainH - 2 * FELT_INSET - 1;
}

/**
 * THE FELT'S STATEMENT PILL — the one line the felt says in its free middle, placed by
 * `auctionCentrePlacement`: the auction's «بالاترین: ۱۳۵ — {name}» (`AuctionCentre`), and the کف's
 * «{name} زمین را برداشت» the other seats read as the حاکم takes it (`shelem-contract.tsx`). One
 * part, so the two can never drift into two looks.
 */
export function FeltPill({
  placement,
  testId,
  snug = false,
  children,
}: {
  placement: ReturnType<typeof auctionCentrePlacement>;
  testId: string;
  /** 8 px a side, not 10 — for a line its pill would cut by a hair (the no-bid line, measured). */
  snug?: boolean;
  children: ReactNode;
}): ReactNode {
  // the tight form (a felt too short for the pill under the partner's column): the line alone
  const tight = placement.form === "tight";
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-10 flex justify-center"
      style={atY(placement.top)}
      data-centre-form={placement.form}
    >
      <div
        data-testid={testId}
        className={`flex min-w-0 items-center gap-2 rounded-full bg-surface-container/85 shadow-lg ${tight || snug ? "px-2" : "px-2.5"}${tight ? "" : " py-1"}`}
        style={{ maxWidth: placement.maxWidth, minHeight: tight ? undefined : PILL_MIN_H }}
      >
        {children}
      </div>
    </div>
  );
}

/** The auction's statement, for every seat (see above). Null while a trick is on the felt. */
export function AuctionCentre({
  view,
  name,
  placement,
  trickOnFelt,
}: {
  view: ShelemView;
  /** The high bidder's name as this viewer reads it (null before any bid). */
  name: string | null;
  placement: ReturnType<typeof auctionCentrePlacement>;
  /** A trick is on the felt — what the felt SHOWS (the table's trick hold), never the frame: the
   *  carried 12th trick stays its whole hold + collect after the auction's first word sweeps it, and
   *  the felt is clear after the collect though the frame still carries it (P1 Task 6). */
  trickOnFelt: boolean;
}): ReactNode {
  const hasHigh = view.highBid > 0 && view.highBidder != null && name !== null;
  // THE NO-BID LINE where the pill would CUT it — «پیشنهادی نیست» (Task 9b, iteration 1: 320×568 ×1.5
  // laid 180.8 px of «هنوز پیشنهادی نیست» out in a 176 px pill, 14.8 px painted past it and onto
  // the left seat's name). Measured in a layout effect, as the redeal's notice is, so no frame paints
  // the cut; the line's own box UNROUNDED with half a pixel of air (the group review's M-1). …And
  // where even that is cut, the pill gives it 2 px a side back (Task 9b, iteration 4: 300×667 ×1.5,
  // the gap's 156 px pill — 137 px of «پیشنهادی نیست» in its 136)
  const noBidRef = useRef<HTMLSpanElement | null>(null);
  const [noBidFit, setNoBidFit] = useState<"long" | "short" | "snug">("long");
  useLayoutEffect(() => {
    const line = noBidRef.current;
    if (
      noBidFit !== "snug" &&
      line !== null &&
      line.scrollWidth > line.getBoundingClientRect().width + 0.5
    )
      setNoBidFit(noBidFit === "long" ? "short" : "snug");
  }, [noBidFit, hasHigh, trickOnFelt, placement.form, placement.maxWidth]);
  if (trickOnFelt) return null;
  return (
    <FeltPill placement={placement} testId="shelem-high-bid" snug={!hasHigh && noBidFit === "snug"}>
      <span
        ref={hasHigh ? undefined : noBidRef}
        className={`m-0 whitespace-nowrap font-body-md text-body-md text-on-surface${hasHigh ? "" : " min-w-0 truncate"}`}
      >
        {hasHigh
          ? // the GAP form (the pill capped to the gap under the partner) drops «بالاترین:» so the
            // name keeps its room — and so does the tight one; the full statement wherever the band
            // holds it (see above)
            placement.form !== "wide"
            ? fa.shelem.highestShort(faDigits(view.highBid))
            : fa.shelem.highest(faDigits(view.highBid))
          : noBidFit === "long"
            ? fa.shelem.noBidYet
            : fa.shelem.noBidYetShort}
      </span>
      {hasHigh ? (
        <span
          dir="auto"
          className="m-0 min-w-0 truncate font-body-md text-body-md font-bold text-secondary"
        >
          {name}
        </span>
      ) : null}
    </FeltPill>
  );
}

/**
 * THE REDEAL's NOTICE (D6; the final review B-I2) — the operator's «سه پاس؛ دست دوباره پخش می‌شه»
 * (card-98 item 5) in the felt's statement pill, in the auction's centre's place, while the new hand
 * deals in (after the third «پاس» has been seen on its seat). WHOLE in every form of the pill: where
 * one line would be cut — the GAP and TIGHT forms under the partner, a band's pill at 300×667 ×1.3
 * (240 px of text in a 228 px pill) — it breaks between its words inside the pill (`min-w-0`, no
 * `truncate`), its lines centred. (Until card-98 it fell back to «دوباره پخش» alone there, which
 * dropped the rest of the sentence.) No animation of its own: it is up for the whole read
 * (`REDEAL_NOTICE_MS`), and the layout kit measures it painted as the deal-in ends (`felt-line-cut`:
 * every line inside its pill).
 *
 * …AND A WRAPPED PILL STAYS CENTRED IN THE BAND. The WIDE form places the pill for ONE line, centred
 * in the band under the side seats (`auctionCentrePlacement`); a sentence on two lines grew DOWN from
 * there — at 300×667 and 320×568 under a 150 % zoom its foot ran onto the felt's gold ring and past it,
 * in bands that hold the two lines whole (91 / 93 px for a 68 px pill). So the pill rises half of
 * every line past its first (`redealLift`), measured in a layout effect — no frame paints it low. The
 * gap and tight forms sit at the partner's floor or the felt's middle: there a wrapped pill grows
 * down into the free felt, never up into the partner's column. Measured once per PLACEMENT (its form,
 * top and width — what moves the line's wrap or the band), not on every table render: the deal-in
 * re-renders the table under the notice, and each read is a forced layout (the review's B-M1).
 */
export function RedealNotice({
  placement,
}: {
  placement: ReturnType<typeof auctionCentrePlacement>;
}): ReactNode {
  const lineRef = useRef<HTMLSpanElement | null>(null);
  const [lift, setLift] = useState(0);
  const { form, top, maxWidth } = placement;
  useLayoutEffect(() => {
    const line = lineRef.current;
    const next =
      line !== null && form === "wide" && PX.test(top)
        ? redealLift(
            line.getBoundingClientRect().height,
            parseFloat(getComputedStyle(line).lineHeight),
          )
        : 0;
    if (Math.abs(next - lift) > 0.25) setLift(next);
  }, [form, top, maxWidth, lift]);
  const placed =
    lift > 0
      ? { ...placement, top: `${(parseFloat(placement.top) - lift).toFixed(1)}px` }
      : placement;
  return (
    <FeltPill placement={placed} testId="shelem-redeal-notice">
      <span
        ref={lineRef}
        className="m-0 min-w-0 text-center font-body-md text-body-md text-on-surface"
      >
        {fa.shelem.redeal}
      </span>
    </FeltPill>
  );
}

/** A placement's top in plain px — the measured forms' (`auctionCentrePlacement`). */
const PX = /^-?\d+(\.\d+)?px$/;

/** How far a wrapped line's pill rises to stay centred where a ONE-line pill was centred: half of
 *  every line past the first. 0 for one line, an unlaid-out line (happy-dom, a hidden page) and a
 *  line height the browser reports as «normal». */
function redealLift(textHeight: number, lineHeight: number): number {
  if (!(lineHeight > 0) || !(textHeight > lineHeight * 1.5)) return 0;
  return (textHeight - lineHeight) / 2;
}

/*
 * THE AUCTION PANEL (card-67 C1 / C3) — ONE frame above your strip, up on YOUR auction turn only
 * (`legal` holds a bid or a pass): the seats' slots · the ladder · «پاس» and «ثبت» (`BidRow`).
 * The حاکم's «کنار بگذار» is NOT in this frame (card-67 C2, the operator: «The «کنار بگذار» box:
 * smaller, so the table doesn't jump between bidding and discarding and the cards below have more
 * room to pick from»): it is one `h-9` button under the contract bar (`ShelemTable`), the size of
 * this panel's «ثبت». A box the bidding panel's height made the felt drop MORE when the discard
 * opened (142 / 146 / 151 px at 384×823 ×1 / ×1.3 / ×1.5, against 94 / 98 / 101 before card-67):
 * the panel is up only on your turn, so the others' passes that end most auctions leave the felt
 * with no panel at all, and the discard box opened from nothing.
 */

/** A row of the auction panel under its slots — the ladder, the buttons — each exactly 36 px
 *  (`auctionPanelHeight` counts them). */
export const AUCTION_ROW = "flex h-9 items-center gap-2";

/** The auction panel's height at a text zoom — its `p-2`, the slots row (two lines, each the 16 px
 *  face or the name's 11 px `leading-tight` line, `gap-0.5` between), then two `AUCTION_ROW`s, 6 px
 *  apart: 134 px at 100 %, 137.75 at ×1.3, 143.25 at ×1.5 (measured 134 / 137.7 / 143.3). The felt is
 *  that much (and the panel's 8 px gap) shorter while it is up — what the کف's corner is sized for
 *  (card-67 C4: one size through the whole auction). */
export function auctionPanelHeight(zoom: number): number {
  const line = Math.max(16, 11 * 1.25 * zoom);
  return 2 * 8 + (2 * line + 2) + 2 * (6 + 36);
}

/**
 * A BID TILE'S WIDTH (card-67 C1) — at least the 52 px tile it has always been, else the widest bid
 * it can hold, «۱۳۵» (1.5825 em of `body-lg`'s 600 weight, off Vazirmatn's `hmtx`), plus its 16 px of
 * padding: so every number tile is ONE width at a given text zoom (54 px at ×1.5, where «۱۳۵»
 * outgrows the floor; 52 below), and «پاس» is two of them exactly (`PASS_W`).
 */
export const BID_TILE_W = "max(52px, calc(1.59em + 16px))";
/** The ladder's gap between two tiles (`gap-1.5`). */
export const LADDER_GAP_PX = 6;
/** «پاس» — AS WIDE AS TWO NUMBER TILES (card-67 C1, the operator: ««پاس» as wide as two number
 *  buttons, and «ثبت» a little smaller to match»): two tiles and the gap between them, in the same
 *  `body-lg` em — so it sits under the ladder's first two tiles at every zoom. «ثبت» takes the rest of
 *  its row, which is that much narrower («a little smaller»: 259 → 226 px at the 384 px phone ×1.3). */
export const PASS_W = `calc(2 * ${BID_TILE_W} + ${LADDER_GAP_PX}px)`;

/** The panel's frame — the bidding row's look since P1 Task 4: one `rounded-2xl` box, inset, its rows
 *  6 px apart, revealed with the app's shared `bg-slidein` (no fill: the box holds its own place).
 *  Where the table cannot hold it at all (card-67 D: Telegram Desktop's 256×393 under a 130–150 %
 *  text zoom — the felt already 0 px) it gives way FROM ITS TOP (`min-h-0 overflow-hidden
 *  justify-end`): the ladder and «پاس» / «ثبت» stay whole, and your strip keeps its one place. */
function AuctionPanel({ testId, children }: { testId: string; children: ReactNode }): ReactNode {
  return (
    <div
      data-testid={testId}
      className="flex min-h-0 flex-col justify-end gap-1.5 overflow-hidden rounded-2xl bg-surface-container-highest p-2"
      style={{
        boxShadow: "inset 0 2px 6px rgba(0,0,0,.4)",
        animation: "bg-slidein .2s ease-out",
      }}
    >
      {children}
    </div>
  );
}

/** What a seat's slot says after its name: «پاس», its bid, or «شلم» for the call — the standing bid
 *  gold. Null before the seat has spoken (its slot keeps the room). */
export function slotWord(
  view: ShelemView,
  seat: number,
): { text: string; tone: "pass" | "high" | "bid" } | null {
  if (view.passed[seat] === true) return { text: fa.shelem.pass, tone: "pass" };
  const bid = view.bids[seat] ?? null;
  if (bid === null) return null;
  const standing = view.highBidder === seat;
  return {
    text: standing && view.called ? fa.shelem.title : faDigits(bid),
    tone: standing ? "high" : "bid",
  };
}

/** The word's box: just over the widest word a slot can hold — «پاس» (1.8081 em at 800), «شلم»
 *  1.7646, «۱۳۵» 1.6104 — so a word arriving moves nothing in its slot (the bottom bar's way, `HUD_EM`). */
export const SLOT_WORD_EM = "1.82em";

/** The two halves, their seats in a FIXED order: yours — you, then your partner — and theirs — the
 *  seat on your right (seat+1), then your left (seat+3), as the felt draws them. */
export function bidSlotHalves(you: number, seats: number): { ours: number[]; theirs: number[] } {
  return { ours: [you, (you + 2) % seats], theirs: [(you + 1) % seats, (you + 3) % seats] };
}

/**
 * THE SEATS' SLOTS (card-67 C3, the operator: «Above the bid numbers: two fixed halves, our team and
 * theirs, with a fixed slot per player — avatar and name from the start, then their bid or «پاس» as
 * the auction goes round. Fixed widths, nothing cut off.») — the history line's place (it listed only
 * the seats that had spoken, in the auction's order, so a word arriving moved every chip after it).
 * Two halves of ONE width each (`flex-1 basis-0`, whatever their names), yours first — on the right,
 * under rtl, as the score box's «ما» — then theirs, a rule between; in each, one slot per seat in a
 * fixed order (`bidSlotHalves`), on its own line: the seat's face (its photo where it has one — the
 * table's `avatarOf`), its name in the team's colour, and its word in a fixed box at the line's end
 * (`slotWord`, `SLOT_WORD_EM`). Two lines a half, not four slots in one line: a half's whole width less
 * the face and the word box is the name's — 2× the room a quarter's line would leave it.
 * A NAME TOO LONG FOR ITS HALF is cut at its END with «…», its start kept (`dir="auto"`: an rtl box
 * would cut a Latin name's beginning) — the seats' own rule; nothing else in the slot is ever cut.
 */
export function BidSlots({
  view,
  nameOf,
  avatarOf,
}: {
  view: ShelemView;
  nameOf: (seat: number) => string;
  avatarOf: (seat: number) => SeatAvatar;
}): ReactNode {
  const { ours, theirs } = bidSlotHalves(view.you, view.seats);
  const half = (seats: number[], mine: boolean): ReactNode => (
    <div
      role="list"
      aria-label={mine ? fa.shelem.us : fa.shelem.them}
      data-slot-half={mine ? "ours" : "theirs"}
      className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5"
    >
      {seats.map((seat) => {
        const face = avatarOf(seat);
        const word = slotWord(view, seat);
        const tone =
          word?.tone === "pass"
            ? "text-on-surface-variant"
            : word?.tone === "high"
              ? "text-secondary"
              : "text-on-surface";
        return (
          <div
            key={seat}
            role="listitem"
            data-slot-seat={seat}
            className="flex min-w-0 items-center gap-1 text-[11px] font-bold leading-tight"
          >
            <Avatar avatarId={face.avatarId} avatarPhotoId={face.avatarPhotoId} size={16} />
            <span
              dir="auto"
              className={`m-0 min-w-0 truncate ${mine ? "text-primary" : "text-error"}`}
            >
              {nameOf(seat)}
            </span>
            <span
              data-slot-word=""
              className={`m-0 ms-auto inline-block shrink-0 whitespace-nowrap text-center font-extrabold ${tone}`}
              style={{ width: SLOT_WORD_EM }}
            >
              {word?.text ?? ""}
            </span>
          </div>
        );
      })}
    </div>
  );
  return (
    <div
      data-testid="shelem-bid-slots"
      role="group"
      aria-label={fa.shelem.bidHistory}
      className="flex gap-1.5"
    >
      {half(ours, true)}
      <span aria-hidden="true" className="w-px shrink-0 self-stretch bg-outline-variant" />
      {half(theirs, false)}
    </div>
  );
}

/**
 * THE BIDDING ROW — yours alone, ABOVE the strip (the strip and the timer in it never move): the
 * auction panel's bidding mode. Three rows:
 *   · the seats' slots (`BidSlots`, card-67 C3) — every seat's face, name and word, the standing
 *     bid gold: where the felt cannot hold the auction's statement (B3 item 7, `auctionCentreFits`)
 *     the slots say the high bid and its bidder, so no second line is needed;
 *   · the ladder of the server's legal raises in a scroller, the cheapest preselected
 *     (`aria-pressed`), each tile ONE width (`BID_TILE_W`), and the separate gold «شلم» tile at its
 *     end, when the call is legal;
 *   · «پاس», two tiles wide (`PASS_W`), and «ثبت N» (or «ثبت شلم») in the rest — which SEND, once per
 *     frame (the table's latch).
 * No prompt line: the strip's pill already says «نوبت شماست». The table derives every control from
 * the server's `legal` list and owns the selection and the send; the row only draws them.
 */
export function BidRow({
  view,
  nameOf,
  avatarOf,
  bidMoves,
  effectiveBid,
  canCall,
  canPass,
  ladderRef,
  onSelect,
  onSend,
}: {
  view: ShelemView;
  nameOf: (seat: number) => string;
  avatarOf: (seat: number) => SeatAvatar;
  /** The server's legal raises, cheapest first. */
  bidMoves: readonly number[];
  /** The standing selection (the pressed tile, the confirm's word). */
  effectiveBid: BidChoice | null;
  canCall: boolean;
  canPass: boolean;
  ladderRef: RefObject<HTMLDivElement>;
  /** A tile or the call tile was tapped. */
  onSelect: (choice: BidChoice) => void;
  /** «پاس» or «ثبت»: the move to send and its cue. */
  onSend: (move: ShelemMove, cue: SfxName) => void;
}): ReactNode {
  return (
    <AuctionPanel testId="shelem-bid-row">
      <BidSlots view={view} nameOf={nameOf} avatarOf={avatarOf} />
      <div className={AUCTION_ROW}>
        <div
          ref={ladderRef}
          className="flex min-w-0 flex-1 snap-x gap-1.5 overflow-x-auto"
          style={{ scrollbarWidth: "none" }}
        >
          {bidMoves.map((amount) => {
            const selected = amount === effectiveBid;
            return (
              <button
                key={amount}
                type="button"
                aria-pressed={selected}
                onClick={() => onSelect(amount)}
                className={`${TABLE_BTN} flex h-9 shrink-0 snap-center items-center justify-center rounded-lg px-2 font-body-lg text-body-lg shadow transition-colors active:scale-95 ${
                  selected
                    ? "bg-primary-container text-on-primary-container ring-2 ring-inset ring-primary"
                    : "bg-surface-container-high text-on-surface hover:bg-surface-bright"
                }`}
                style={{ width: BID_TILE_W }}
              >
                {bidLabel(amount)}
              </button>
            );
          })}
        </div>
        {/* The gold tile is the شلم CALL only — never a number (P0-3) — in its own slot at
            the ladder's end, never scrolled out of reach. */}
        {canCall ? (
          <button
            type="button"
            data-testid="shelem-call"
            aria-pressed={effectiveBid === "shelem"}
            onClick={() => onSelect("shelem")}
            className={`${TABLE_BTN} flex h-9 min-w-[52px] shrink-0 items-center justify-center rounded-lg bg-secondary-container px-3 font-body-lg text-body-lg text-secondary shadow transition-colors active:scale-95 ${
              effectiveBid === "shelem" ? "ring-2 ring-inset ring-primary" : ""
            }`}
          >
            {bidLabel("shelem")}
          </button>
        ) : null}
      </div>
      <div className={AUCTION_ROW}>
        {canPass ? (
          <button
            type="button"
            onClick={() => onSend({ type: "pass" }, "pass")}
            className={`${TABLE_BTN} flex h-9 flex-none items-center justify-center rounded-lg border-0 bg-danger-bluff font-body-lg text-body-lg text-on-error shadow-lg transition-transform active:scale-95`}
            style={{ width: PASS_W }}
          >
            {fa.shelem.pass}
          </button>
        ) : null}
        {effectiveBid != null ? (
          <button
            type="button"
            onClick={() =>
              effectiveBid === "shelem"
                ? onSend({ type: "shelem" }, "shelemCall")
                : onSend({ type: "bid", amount: effectiveBid }, "bid")
            }
            className={`${TABLE_BTN} flex h-9 min-w-0 flex-1 items-center justify-center rounded-lg border-0 bg-secondary font-body-lg text-body-lg text-ink-on-gold transition-transform active:scale-95`}
            style={{ boxShadow: "0 4px 14px rgba(232,194,104,.4)" }}
          >
            {fa.shelem.confirmBid(bidLabel(effectiveBid))}
          </button>
        ) : null}
      </div>
    </AuctionPanel>
  );
}
